"use client";
import { motion, Variants } from "framer-motion";


type Props = { text: string; instant?: boolean };

// Base gap between words after their letters finish animating
const WORD_GAP = 0.12;

// Compute letter stagger per word based on its length. Shorter words get
// slightly larger per-letter stagger for readability; longer words get
// a tighter stagger so the whole word doesn't take too long.
function letterStaggerFor(word: string) {
  const len = Math.max(1, word.length);
  const base = 0.08; // baseline for medium words
  const stagger = (base * 6) / len; // inversely proportional to length
  return Math.min(0.12, Math.max(0.02, stagger));
}

const containerVariants: Variants = {
  hidden: {},
  // custom is expected to be an object: { delay: number, stagger: number }
  visible: (custom: any = { delay: 0, stagger: 0.06 }) => ({
    transition: {
      delayChildren: custom.delay,
      staggerChildren: custom.stagger,
    },
  }),
};

const letterVariants: Variants = {
  hidden: { opacity: 0, y: 8, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function LetterReveal({ text, instant }: Props) {
  // Group letters by words so we never break inside a word.
  const words = text.split(" ");
  // Precompute per-word stagger and cumulative delays so each word's
  // animation starts after the previous word finished its letters.
  const params = [] as { delay: number; stagger: number }[];
  let cumulativeDelay = 0;
  for (const w of words) {
    const stagger = letterStaggerFor(w);
    params.push({ delay: cumulativeDelay, stagger });
    cumulativeDelay += w.length * stagger + WORD_GAP;
  }
  if (instant) {
    // If instant is true, override all delays to 0 so letters appear immediately
    for (const p of params) {
      p.delay = 0;
      p.stagger = 0;
    }
  }

  return (
    <>
      {words.map((word, wi) => (
        <motion.span
          // include the word text in the key so identical words at different
          // positions still get unique identities and can animate independently
          key={`${word}-${wi}-${text}`}
          style={{
            display: "inline-block",
            whiteSpace: "nowrap",
            lineHeight: 1.15,
          }}
          variants={containerVariants}
          initial={instant ? "visible" : "hidden"}
          animate="visible"
          custom={params[wi]}
        >
          {word.split("").map((char, i) => (
            <motion.span
              // make letter keys unique using word, index and character so
              // repeated letters/words don't accidentally reuse DOM nodes
              key={`${word}-${wi}-${char}-${i}`}
              variants={letterVariants}
              style={{ display: "inline-block", lineHeight: 1.15 }}
            >
              {char}
            </motion.span>
          ))}
          {wi < words.length - 1 ? (
            <span style={{ display: "inline-block", width: "0.35em" }}>
              &nbsp;
            </span>
          ) : null}
        </motion.span>
      ))}
    </>
  );
}
